import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import UploadCloudIcon from '@fingo/lib/assets/icons/itau/UploadCloudIcon';
import useSnackBars from '@fingo/lib/hooks/useSnackBars';
import UploadDropZoneDialogContainer from '@fingo/lib/components/containers/UploadDropZoneDialogContainer';
import INVOICE_UPLOAD_DROPZONE_CLASS_NAME from '@fingo/lib/theme/constants/upload-dropzone';

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const NOMINA_ACCEPTED_EXTENSIONS = '.xlsx,.xls';
const NOMINA_ACCEPT = {
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

const NominaDropZoneContent = ({ files }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2.875}>
      <UploadCloudIcon
        width={40}
        height={40}
        style={{ flexShrink: 0, color: theme.palette.secondary.main }}
      />
      <Stack spacing={0.25} alignItems="flex-start">
        {files.length ? (
          <Typography variant="medium_2" fontWeight={700} sx={{ color: 'gray.A700' }}>
            {files[0].name}
          </Typography>
        ) : (
          <>
            <Typography variant="medium_2" fontWeight={700} sx={{ color: 'gray.A700' }}>
              Sube o arrastra las nóminas
            </Typography>
            <Typography variant="small_3" sx={{ color: 'gray.A500' }}>
              {`Tamaño máximo de archivo: ${MAX_FILE_SIZE_MB}MB`}
            </Typography>
            <Typography variant="small_3" sx={{ color: 'gray.A500' }}>
              Formatos admitidos: .xlsx, .xls
            </Typography>
          </>
        )}
      </Stack>
    </Stack>
  );
};

NominaDropZoneContent.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
};

const UploadNominaDropZone = ({ files, setFiles }) => {
  const { addAlert } = useSnackBars();

  const handleReject = useCallback((fileRejections) => {
    const isTooLarge = fileRejections[0].errors.some((error) => error.code === 'file-too-large');
    if (isTooLarge) {
      addAlert({
        id: 'upload-nomina-rejected-file',
        message: `El archivo supera el tamaño máximo de ${MAX_FILE_SIZE_MB}MB`,
        severity: 'error',
      });
      return;
    }
    addAlert({
      id: 'upload-nomina-rejected-file',
      message: `Archivos permitidos: ${NOMINA_ACCEPTED_EXTENSIONS}`,
      severity: 'warning',
      color: 'warning',
    });
  }, [addAlert]);

  return (
    <UploadDropZoneDialogContainer
      files={files}
      setFiles={setFiles}
      accept={NOMINA_ACCEPT}
      maxSize={MAX_FILE_SIZE_BYTES}
      multiple={false}
      onReject={handleReject}
      className={INVOICE_UPLOAD_DROPZONE_CLASS_NAME}
      renderContent={NominaDropZoneContent}
    />
  );
};

UploadNominaDropZone.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  setFiles: PropTypes.func.isRequired,
};

export default UploadNominaDropZone;
